import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faQuestionCircle, faChevronDown, faChevronUp, faSearch,
    faHome, faFilter, faBookmark, faClock
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface FAQ {
    id: number;
    question: string;
    answer: string;
    category: string;
    tags: string[];
    helpful: number;
    notHelpful: number;
    lastUpdated: string;
    isBookmarked?: boolean;
}

const FAQs: React.FC = () => {
    const [faqs, setFaqs] = useState<FAQ[]>([]);
    const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "FAQs - Uzima Healthcare";
        loadClinicData();
        loadFAQs();
    }, []);

    useEffect(() => {
        filterFAQs();
    }, [faqs, searchQuery, selectedCategory]);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadFAQs = () => {
        // Mock FAQ data - in real app, fetch from API
        const mockFAQs: FAQ[] = [
            {
                id: 1,
                question: "How do I schedule a telehealth session?",
                answer: "To schedule a telehealth session, go to the Telehealth Center and click 'Schedule Session'. Fill in the session details including title, date, time, and participants. You can add participants by their email address, and the system will validate if they exist in the database.",
                category: "Telehealth",
                tags: ["telehealth", "scheduling", "sessions"],
                helpful: 45,
                notHelpful: 2,
                lastUpdated: "2025-09-05"
            },
            {
                id: 2,
                question: "What are the different session tiers available?",
                answer: "Uzima offers three session tiers: Basic (10 people, 30min max), Standard (20 people, 2hrs max), and Premium (unlimited people and duration). Each tier includes different features like recording, breakout rooms, and live streaming.",
                category: "Telehealth",
                tags: ["tiers", "limits", "features"],
                helpful: 38,
                notHelpful: 1,
                lastUpdated: "2025-09-05"
            },
            {
                id: 3,
                question: "How do I add participants to a session?",
                answer: "Participants are added by email address. Enter the participant's email in the 'Add Participants by Email' field. The system will validate the email and check if the user exists in the database. Once validated, click 'Add Participant' to include them in the session.",
                category: "Telehealth",
                tags: ["participants", "email", "validation"],
                helpful: 52,
                notHelpful: 3,
                lastUpdated: "2025-09-05"
            },
            {
                id: 4,
                question: "How do I create a service request?",
                answer: "Navigate to the Service Center and click 'Create Service Request'. The multi-step form will guide you through: Basic Information, Patient Details (with ID validation), Service Requirements, Contract & Partners, Payment Terms, and Information Sharing preferences.",
                category: "Services",
                tags: ["service-request", "multi-step", "form"],
                helpful: 67,
                notHelpful: 4,
                lastUpdated: "2025-09-04"
            },
            {
                id: 5,
                question: "How do I validate a patient ID?",
                answer: "When creating a service request, enter the patient ID in the Patient Details section. The system will automatically validate the patient ID when you leave the textbox, checking if the patient belongs to your clinic and is active in the system.",
                category: "Services",
                tags: ["patient", "validation", "id"],
                helpful: 41,
                notHelpful: 2,
                lastUpdated: "2025-09-04"
            },
            {
                id: 6,
                question: "What is the billing system?",
                answer: "The Uzima billing system handles payments, invoicing, and financial transactions. It includes wallet functionality, payment processing, and billing notifications. You can manage your billing account, view transactions, and set up payment methods.",
                category: "Billing",
                tags: ["billing", "payments", "wallet"],
                helpful: 29,
                notHelpful: 1,
                lastUpdated: "2025-09-03"
            },
            {
                id: 7,
                question: "How do I manage IoT devices?",
                answer: "IoT devices can be managed through the Telehealth Center. You can view device status, refresh readings, connect/disconnect devices, and monitor battery levels. Devices are automatically detected when connected to the system.",
                category: "IoT",
                tags: ["iot", "devices", "monitoring"],
                helpful: 33,
                notHelpful: 2,
                lastUpdated: "2025-09-03"
            },
            {
                id: 8,
                question: "How do I access notifications?",
                answer: "Notifications are available in the Notifications section of the dashboard. You can filter by type (billing, wallet, system), mark as read/unread, and manage notification preferences. The system sends real-time notifications for important events.",
                category: "Notifications",
                tags: ["notifications", "alerts", "preferences"],
                helpful: 44,
                notHelpful: 3,
                lastUpdated: "2025-09-02"
            }
        ];
        setFaqs(mockFAQs);
    };

    const filterFAQs = () => {
        let filtered = faqs;

        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(faq => 
                faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(faq => faq.category === selectedCategory);
        }

        setFilteredFaqs(filtered);
    };

    const categories = ['all', 'Telehealth', 'Services', 'Billing', 'IoT', 'Notifications'];

    const toggleFaq = (id: number) => {
        setExpandedFaq(expandedFaq === id ? null : id);
    };

    const handleHelpful = (id: number, isHelpful: boolean) => {
        setFaqs(prevFaqs => 
            prevFaqs.map(faq => 
                faq.id === id 
                    ? { 
                        ...faq, 
                        helpful: isHelpful ? faq.helpful + 1 : faq.helpful,
                        notHelpful: !isHelpful ? faq.notHelpful + 1 : faq.notHelpful
                    }
                    : faq
            )
        );
    };

    const toggleBookmark = (id: number) => {
        setFaqs(prevFaqs => 
            prevFaqs.map(faq => 
                faq.id === id 
                    ? { ...faq, isBookmarked: !faq.isBookmarked }
                    : faq
            )
        );
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2 text-primary" />
                                            Frequently Asked Questions
                                        </h2>
                                        <p className="text-muted mb-0">
                                            Find answers to common questions about {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-primary fs-6 px-3 py-2">
                                            {filteredFaqs.length} Questions
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-8">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search FAQs..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <select
                                    className="form-select"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    {categories.map(category => (
                                        <option key={category} value={category}>
                                            {category === 'all' ? 'All Categories' : category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* FAQ List */}
                        <div className="row">
                            <div className="col-12">
                                {filteredFaqs.map((faq) => (
                                    <div key={faq.id} className="card border-0 shadow-sm mb-3">
                                        <div 
                                            className="card-header bg-white border-0"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleFaq(faq.id)}
                                        >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center">
                                                    <FontAwesomeIcon 
                                                        icon={expandedFaq === faq.id ? faChevronUp : faChevronDown} 
                                                        className="text-muted me-3"
                                                    />
                                                    <div>
                                                        <h6 className="mb-1">{faq.question}</h6>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <span className="badge bg-primary bg-opacity-20 text-primary">
                                                                {faq.category}
                                                            </span>
                                                            <small className="text-muted">
                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                Updated {faq.lastUpdated}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleBookmark(faq.id);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon 
                                                            icon={faBookmark} 
                                                            className={faq.isBookmarked ? 'text-warning' : 'text-muted'}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {expandedFaq === faq.id && (
                                            <div className="card-body">
                                                <p className="mb-3">{faq.answer}</p>
                                                
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span className="text-muted small">Was this helpful?</span>
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => handleHelpful(faq.id, true)}
                                                        >
                                                            <FontAwesomeIcon icon={faChevronUp} className="me-1" />
                                                            {faq.helpful}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleHelpful(faq.id, false)}
                                                        >
                                                            <FontAwesomeIcon icon={faChevronDown} className="me-1" />
                                                            {faq.notHelpful}
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="d-flex gap-2">
                                                        {faq.tags.map((tag, index) => (
                                                            <span key={index} className="badge bg-light text-dark">
                                                                #{tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* No Results */}
                        {filteredFaqs.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faQuestionCircle} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No FAQs found</h5>
                                <p className="text-muted">
                                    Try adjusting your search terms or category filter
                                </p>
                            </div>
                        )}

                        {/* Contact Support */}
                        <div className="row mt-5">
                            <div className="col-12">
                                <div className="card border-0 bg-light">
                                    <div className="card-body text-center py-4">
                                        <h5 className="mb-3">Still have questions?</h5>
                                        <p className="text-muted mb-4">
                                            Can't find the answer you're looking for? Our support team is here to help.
                                        </p>
                                        <button className="btn btn-primary">
                                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                                            Contact Support
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default FAQs;
